import type { Language } from '@ps/poker-games-tile-mappers';
import { mappers } from '@ps/poker-games-tile-mappers';
import type { RecentTournament, TableStaticData } from '@ps/web-shared-api-types/v0/SwHomeGames';
import type { HgScheduledRingGame } from '@ps/web-shared-api-types/v1-preview/SwHomeGames';

import { PKR_INITIAL_GAMES_DATA, TOURNFLAG_SERVINT_RECURRING_TOURN } from '../constants';
import type { GamesByClubs, HGPokerGameTile } from '../types/common';

type CopyProp = 'id' | 'serverObj';
type TargetProp = 'tableId' | 'serverObject';
type ScheduledTable = TableStaticData & HgScheduledRingGame;

export const copyPropValue = (
  game: TableStaticData,
  copyProp: CopyProp,
  targetProp: TargetProp
): TableStaticData => {
  if (game?.[copyProp]) {
    return game;
  }

  if (game && !game[targetProp]) {
    return game;
  }

  return {
    ...game,
    [copyProp]: game?.[targetProp]
  };
};

export const arrangeByClubs = (memo: GamesByClubs, item: HGPokerGameTile) => {
  const { clubId } = item;

  if (!clubId) return memo;

  return {
    ...memo,
    [clubId]: memo[clubId] ? [...memo[clubId], item] : [item]
  };
};

export const mapTableType = (
  tableGames: ScheduledTable[],
  language: Language,
  timeZone?: string
) => {
  if (!tableGames) return [];

  try {
    return mappers
      .mapTableTypes(
        tableGames
          // There is a mismatch between serverObj and serverObject fields in the response
          // which comes from Core Server API which we don't want to diverge from.
          // Add serverObj field if missing or if replaced by serverObject.
          .map((game) => copyPropValue(game, 'serverObj', 'serverObject')) // serverObject => serverObj
          // tableId should become id in order to normalize as per poker-games-tile-mappers expectations.
          .map((game) => copyPropValue(game, 'id', 'tableId')), // tableId => id
        { language, timeZone } as never
      )
      .map((game, index) => ({
        ...game,
        clubId: tableGames[index]?.clubId ?? 0,
        supported: tableGames[index].supported,
        name: tableGames[index].name,
        maxPlayers: tableGames[index].maxPlayers,
        handType: tableGames[index].handType,
        potStruct: tableGames[index].potStruct,
        tableStakes: tableGames[index].tableStakes,
        playersSeated: tableGames[index].playersSeated
      }))
      .reduce(arrangeByClubs, PKR_INITIAL_GAMES_DATA);
  } catch (error) {
    return [];
  }
};

export const mapTournType = (
  tournaments: RecentTournament[],
  language: Language,
  timeZone?: string
) => {
  if (!tournaments) return [];

  try {
    return mappers
      .mapMttTypes(tournaments, { language, timeZone } as never)
      .map((game, index) => {
        const tournFlagsServInt = tournaments[index].staticData?.tournFlagsServInt;
        const tourn = {
          ...game,
          clubId: tournaments[index]?.staticData?.clubId ?? 0,
          supported: tournaments[index].supported,
          name: tournaments[index].staticData?.name,
          maxPlayers: tournaments[index].staticData?.maxPerTable,
          handType: tournaments[index].staticData?.handType,
          potStruct: tournaments[index].staticData?.potStruct,
          tournFlagsServInt: tournaments[index].staticData?.tournFlagsServInt,
          whenStart: tournaments[index].staticData?.whenStart?.toString(),
          entrants: tournaments[index].dynamicData?.entrants
        };

        return tournFlagsServInt
          ? {
              ...tourn,
              isRecurring:
                // eslint-disable-next-line no-bitwise
                (tournFlagsServInt & TOURNFLAG_SERVINT_RECURRING_TOURN) ===
                TOURNFLAG_SERVINT_RECURRING_TOURN
            }
          : tourn;
      })
      .reduce(arrangeByClubs, PKR_INITIAL_GAMES_DATA);
  } catch (error) {
    return [];
  }
};
